import { apiClient, ApiError } from '../lib/apiClient';

export const DOSING_SCHEDULE_FREQUENCIES = [
  'TIMES_PER_DAY',
  'EVERY_X_DAYS',
  'TIMES_PER_WEEK',
  'SPECIFIC_DAYS',
  'WEEKLY',
  'EVERY_X_WEEKS',
] as const;

export type DosingScheduleFrequency = (typeof DOSING_SCHEDULE_FREQUENCIES)[number];

export type DosingPlanStatus = 'active' | 'paused' | 'scheduled' | 'ended';

/**
 * DosingSchedule shape mirrors the backend API response exactly — field names
 * are snake_case to match the persisted database columns and the JSON the
 * API returns. Consumers (components) must reference these exact field names
 * (e.g. `schedule.product_name`, not `schedule.productName`).
 */
/** Reef Guard dose guard settings of a plan (#1822). */
export interface DoseGuardSettings {
  readonly element: 'alkalinity' | 'calcium' | 'magnesium';
  readonly product: 'sangokai_kh' | 'balling_light_kh' | 'custom' | 'kalkwasser';
  /** custom: element unit × litres one ml adds. */
  readonly strength?: number | null;
}

export interface DoseGuardResult {
  readonly status: 'ok' | 'warn' | 'block' | 'strength_required' | 'not_checked' | 'volume_required';
  readonly element: DoseGuardSettings['element'];
  readonly unit: string;
  readonly perDay: number | null;
  readonly mlPerDay: number | null;
  readonly volumeLiters: number | null;
  readonly cap: number | null;
  readonly splitDays: number | null;
  readonly projectionDays: number | null;
  readonly reason: string;
}

/** POST …/dosing-schedules/guard-check — what the plan adds per dosing day vs the caps. */
export async function checkDoseGuard(
  aquariumId: string,
  input: { dose_guard: DoseGuardSettings; dose_amount: number; dose_unit: string; frequency_type: string; frequency_value: number | null },
): Promise<DoseGuardResult> {
  const response = await apiClient.post<{ data: DoseGuardResult }>(`/api/v1/aquariums/${aquariumId}/dosing-schedules/guard-check`, input);
  return response.data;
}

export interface DosingSchedule {
  readonly dose_guard?: DoseGuardSettings | null;
  readonly id: string;
  readonly tank_id: string;
  readonly product_name: string;
  readonly dose_amount: number;
  readonly dose_unit: string;
  readonly frequency: DosingScheduleFrequency;
  readonly weekdays: number[] | null;
  readonly interval_days: number | null;
  readonly times_per_day: number | null;
  readonly time_of_day: string | null;
  readonly notes: string | null;
  /** Local clock times (HH:MM) of a TIMES_PER_DAY plan's doses; null = defaults. */
  readonly dose_times?: string[] | null;
  /** "Dosierung über Dosierpumpe": scheduled doses are logged automatically. */
  readonly dosing_pump?: boolean;
  readonly dosing_pump_enabled_at?: string | null;
  /** Next automatically logged dose (pump plans only). */
  readonly next_auto_log_at?: string | null;
  /** First / last local day of the plan ('YYYY-MM-DD'); null = no restriction. */
  readonly start_date?: string | null;
  readonly end_date?: string | null;
  /** Paused since (null = not paused) and optional auto-resume day. */
  readonly paused_at?: string | null;
  readonly paused_until?: string | null;
  /** Computed by the API from the dates and the pause (Europe/Berlin). */
  readonly status?: DosingPlanStatus;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface CreateDosingScheduleInput {
  readonly dose_guard?: DoseGuardSettings | null;
  readonly product_name: string;
  readonly dose_amount: number;
  readonly dose_unit: string;
  readonly frequency: DosingScheduleFrequency;
  readonly weekdays?: number[] | null;
  readonly interval_days?: number | null;
  readonly times_per_day?: number | null;
  readonly time_of_day?: string | null;
  readonly dose_times?: string[] | null;
  readonly dosing_pump?: boolean;
  readonly start_date?: string | null;
  readonly end_date?: string | null;
  readonly notes?: string | null;
}

export interface UpdateDosingScheduleInput {
  readonly dose_guard?: DoseGuardSettings | null;
  /** true = pause now (until paused_until, if given); false = resume. */
  readonly paused?: boolean;
  readonly paused_until?: string | null;
  readonly product_name?: string;
  readonly dose_amount?: number;
  readonly dose_unit?: string;
  readonly frequency?: DosingScheduleFrequency;
  readonly weekdays?: number[] | null;
  readonly interval_days?: number | null;
  readonly times_per_day?: number | null;
  readonly time_of_day?: string | null;
  readonly dose_times?: string[] | null;
  readonly dosing_pump?: boolean;
  readonly start_date?: string | null;
  readonly end_date?: string | null;
  readonly notes?: string | null;
}

/**
 * Error thrown when the API responds 409 Conflict for a ZeoVit dosing-product
 * combination rule violation. Carries only the verified `reason` field from
 * the response body — never the raw/full response payload.
 */
export class DosingConflictError extends Error {
  public readonly reason: string;
  /** Name of the already scheduled product the new one conflicts with (user data). */
  public readonly existingProductName: string | null;

  constructor(reason: string, existingProductName: string | null = null) {
    super(reason);
    this.name = 'DosingConflictError';
    this.reason = reason;
    this.existingProductName = existingProductName;
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

interface ConflictResponseBody {
  readonly error?: {
    readonly reason?: string;
    readonly message?: string;
    readonly details?: { readonly existingProductName?: unknown };
  };
}

function extractExistingProductName(body: unknown): string | null {
  const name = (body as ConflictResponseBody | undefined)?.error?.details?.existingProductName;
  return typeof name === 'string' && name.length > 0 ? name : null;
}

function extractConflictReason(body: unknown): string {
  const parsed = body as ConflictResponseBody;
  const reason = parsed?.error?.reason;
  if (typeof reason === 'string' && reason.length > 0) {
    return reason;
  }
  const message = parsed?.error?.message;
  if (typeof message === 'string' && message.length > 0) {
    return message;
  }
  return 'A dosing conflict was detected for this product combination.';
}

/**
 * `apiClient` resolves with the parsed JSON body and throws `ApiError` on
 * non-2xx responses (it never returns a `Response`). A 409 is the ZeoVit
 * combination rule and becomes a DosingConflictError.
 */
function rethrowConflict(err: unknown): never {
  if (err instanceof ApiError && err.status === 409) {
    throw new DosingConflictError(extractConflictReason(err.data), extractExistingProductName(err.data));
  }
  throw err;
}

export async function listDosingSchedules(aquariumId: string): Promise<DosingSchedule[]> {
  const json = await apiClient.get<{ data: DosingSchedule[] }>(
    `/api/v1/aquariums/${aquariumId}/dosing-schedules`,
  );
  return json.data;
}

export async function createDosingSchedule(
  aquariumId: string,
  input: CreateDosingScheduleInput,
): Promise<DosingSchedule> {
  try {
    const json = await apiClient.post<{ data: DosingSchedule }>(
      `/api/v1/aquariums/${aquariumId}/dosing-schedules`,
      input,
    );
    return json.data;
  } catch (err) {
    return rethrowConflict(err);
  }
}

export async function updateDosingSchedule(
  aquariumId: string,
  scheduleId: string,
  input: UpdateDosingScheduleInput,
): Promise<DosingSchedule> {
  try {
    const json = await apiClient.patch<{ data: DosingSchedule }>(
      `/api/v1/aquariums/${aquariumId}/dosing-schedules/${scheduleId}`,
      input,
    );
    return json.data;
  } catch (err) {
    return rethrowConflict(err);
  }
}

export async function deleteDosingSchedule(
  aquariumId: string,
  scheduleId: string,
): Promise<void> {
  try {
    await apiClient.delete(`/api/v1/aquariums/${aquariumId}/dosing-schedules/${scheduleId}`);
  } catch (err) {
    rethrowConflict(err);
  }
}

/** Pauses a plan now, optionally until the start of `pausedUntil` ('YYYY-MM-DD'). */
export function pauseDosingSchedule(aquariumId: string, scheduleId: string, pausedUntil: string | null): Promise<DosingSchedule> {
  return updateDosingSchedule(aquariumId, scheduleId, { paused: true, paused_until: pausedUntil });
}

/** Resumes a paused plan from now on (the paused period is not filled in). */
export function resumeDosingSchedule(aquariumId: string, scheduleId: string): Promise<DosingSchedule> {
  return updateDosingSchedule(aquariumId, scheduleId, { paused: false });
}
